import React, {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';
import {
    Button,
    Dialog,
    Flex,
    Title,
    Text,
    PriorityIcon,
    PopoverPlacement,
    Popover,
    CheckBox
} from '@priority-software/priority-style-react';
import {sendMonitor} from '../../utils/monitoring';

import {createOptionsFromCols, getSelectedFields, IColumn} from './utils';
import {AlertMessage} from './components/AlertMessage';
import {SelectedFields} from './components/SelectedFieldList';
import {SearchableFields} from './components/SearchableFields';
import {showSuccessToast} from '../Toast/actions/ToastActions';
import {getTopDialogZindex} from '../../_shared/GWTBridge/invoke/priModalDlg';
import styles from './styles.module.scss';
import texts from '../../features/MoreInfo/texts';
import {isRTL} from '../../Localization';
import {createDataTestId} from '../../utils/dataTestId';
import {Loader} from '../../features/OnboardingDialog/components';

const MAX_FIELDS = 9;

export type SelectedEntity = {
    entityId: string;
    cols: IColumn[];
    selectedFields: string[];
};

type Props = {
    isOpen: boolean;
    updateFields: (entityId: string, fields: Array<string>, cbc?: () => void) => void;
    closeDialog: () => void;
    selectedEntity: SelectedEntity;
    zoomFname?: string;
    defaultFields: string[];
    maxSelectableFields?: number;
    sepcialCheckboxOpt?: string;
    specialCheckboxState?: boolean;
    specialCheckboxText?: string;
    specialHelpText?: string;
    specialTitle?: string;
    zIndex?: number;
};

export const EditingDialog = (props: Props) => {
    const {
        updateFields,
        selectedEntity,
        closeDialog,
        defaultFields,
        maxSelectableFields = MAX_FIELDS,
        sepcialCheckboxOpt,
        specialCheckboxState = false,
        specialCheckboxText,
        specialHelpText,
        specialTitle,
        zIndex = getTopDialogZindex()
    } = props;

    const availableFields = createOptionsFromCols(selectedEntity.cols.filter(col => !col.Hidden));
    const defaultSelected = getSelectedFields(availableFields, defaultFields);

    const [selectedValues, setSelectedValue] = useState(
        getSelectedFields(availableFields, selectedEntity.selectedFields)
    );
    const [showAlertMsg, toggleShowAlertMsg] = useState(false);
    const [isSpecialCheckboxChecked, setIsSpecialCheckboxChecked] = useState(specialCheckboxState);
    const [hasUnsavedChanges, setChangesFlag] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onKeyDown = e => {
        e.stopPropagation();
    };

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyDown);
        };
    }, []);

    const onClose = useCallback(() => {
        if (!hasUnsavedChanges) {
            closeDialog();
        } else {
            toggleShowAlertMsg(true);
        }
    }, [closeDialog, toggleShowAlertMsg, hasUnsavedChanges]);

    const onUpdate = useCallback(() => {
        const selectedFieldsNames = selectedValues.map(f => f.value);
        if (isSpecialCheckboxChecked) {
            selectedFieldsNames.push(sepcialCheckboxOpt);
        }
        setIsLoading(true);
        updateFields(selectedEntity.entityId, selectedFieldsNames, () => {
            closeDialog();
            showSuccessToast(texts.saved_success_msg);
            setIsLoading(false);
            sendMonitor('MoreDetailsEditSave');
        });
    }, [selectedValues, selectedEntity, closeDialog, updateFields, isSpecialCheckboxChecked]);

    const onReorder = useCallback(
        (values, index: number) => {
            const isFixed = values[index]?.fixed;
            const sorted = [...values].sort((a, b) => b.fixed - a.fixed);
            if (!isFixed) {
                setChangesFlag(true);
                setSelectedValue(sorted);
            }
        },
        [setSelectedValue, setChangesFlag]
    );

    const removeSelected = useCallback(
        (id: number) => {
            const updatedData = selectedValues.filter(item => item.id !== id);
            setChangesFlag(true);
            setSelectedValue(updatedData);
        },
        [selectedValues, setChangesFlag]
    );

    const addSelected = useCallback(
        item => {
            if (selectedValues.length < maxSelectableFields) {
                const updatedData = item.fixed
                    ? [item, ...selectedValues]
                    : [...selectedValues, item];

                setSelectedValue(updatedData);
                setChangesFlag(true);
            }
        },
        [selectedValues, setChangesFlag]
    );

    const onReset = useCallback(() => {
        setChangesFlag(true);
        setSelectedValue(defaultSelected);
    }, [defaultSelected, setSelectedValue, setChangesFlag]);

    const onExitWithoutSave = useCallback(() => {
        toggleShowAlertMsg(false);
        setChangesFlag(false);
        closeDialog();
    }, [closeDialog, setChangesFlag, toggleShowAlertMsg]);

    const onSpecialCheckboxChange = () => {
        setIsSpecialCheckboxChecked(!isSpecialCheckboxChecked);
        setChangesFlag(true);
    };

    const btnClasses = cx(styles.btn, styles.btnMinWidth);

    const helpContentText = specialHelpText ? specialHelpText : texts.help_edit_content;
    const dialogTitle = specialTitle ? specialTitle : texts.edit_fields_title;

    return (
        <>
            <Dialog
                isOpen
                width={640}
                zIndex={zIndex}
                onClose={onClose}
                componentClasses={styles.component}
                size="small"
                maskClosable
                title={
                    <Title
                        componentClasses={styles.mainDialogTitle}
                        skin="h4"
                        textColor="primary-700"
                    >
                        {dialogTitle}
                        <Popover
                            {...createDataTestId('popover-edit-dialog-container', true)}
                            clickable
                            closable
                            rtl={isRTL()}
                            hoverable={false}
                            icon={PriorityIcon.questionCircle}
                            title={
                                <p {...createDataTestId('popover-edit-dialog-title', false)}>
                                    {texts.help_label}
                                </p>
                            }
                            content={
                                <p {...createDataTestId('popover-edit-dialog-container', false)}>
                                    {helpContentText}
                                </p>
                            }
                            zIndex={getTopDialogZindex()}
                            placement={PopoverPlacement.bottomEnd}
                            footer={onHide => (
                                <Flex justifyContent="flex-end">
                                    <Button skin="primary" onClick={onHide}>
                                        {texts.ok_label}
                                    </Button>
                                </Flex>
                            )}
                        >
                            <Button
                                componentClasses={styles.icon}
                                icon={PriorityIcon.help}
                                skin="text"
                            />
                        </Popover>
                    </Title>
                }
                content={
                    <div className={styles.dialogContent}>
                        <div className={styles.dialogContentPart}>
                            <div className={styles.section}>
                                <Title
                                    componentClasses={styles.sectionTitle}
                                    textColor="primary-700"
                                    skin="h5"
                                    text={texts.available_fields_title}
                                />
                                <SearchableFields
                                    limit={maxSelectableFields}
                                    data={availableFields}
                                    selected={selectedValues}
                                    addItem={addSelected}
                                    deleteItem={removeSelected}
                                />
                            </div>
                        </div>

                        <div className={styles.dialogContentPart}>
                            <div className={styles.section}>
                                <Title
                                    componentClasses={styles.sectionTitle}
                                    textColor="primary-700"
                                    skin="h5"
                                >
                                    {texts.selected_title}
                                    <Text
                                        skin="label-1"
                                        textColor="secondary-500"
                                        tag="span"
                                        componentClasses={styles.counter}
                                    >
                                        {selectedValues.length}/{maxSelectableFields}
                                    </Text>
                                </Title>
                                <SelectedFields
                                    data={selectedValues}
                                    onReorder={onReorder}
                                    deleteItem={removeSelected}
                                />
                            </div>
                        </div>
                    </div>
                }
                footer={
                    <>
                        {!!sepcialCheckboxOpt && (
                            <CheckBox
                                children={specialCheckboxText}
                                checked={isSpecialCheckboxChecked}
                                onChange={onSpecialCheckboxChange}
                            />
                        )}
                        <Flex componentClasses={styles.footer}>
                            <Button
                                size="large"
                                skin="text"
                                onClick={onReset}
                                componentClasses={styles.resetBtn}
                            >
                                {texts.btn_reset}
                            </Button>

                            <Flex justifyContent="flex-end">
                                <Button
                                    componentClasses={btnClasses}
                                    skin="secondary"
                                    size="medium"
                                    onClick={onClose}
                                >
                                    {texts.btn_cancel}
                                </Button>
                                <Button
                                    componentClasses={btnClasses}
                                    size="medium"
                                    skin="primary"
                                    onClick={onUpdate}
                                    disabled={!hasUnsavedChanges}
                                    suffixIcon={
                                        isLoading && <Loader componentClasses={styles.loader} />
                                    }
                                >
                                    {texts.btn_save}
                                </Button>
                            </Flex>
                        </Flex>
                    </>
                }
            />
            {
                <AlertMessage
                    isOpen={showAlertMsg}
                    closeAlertDialog={() => toggleShowAlertMsg(false)}
                    exitWithoutSave={onExitWithoutSave}
                />
            }
        </>
    );
};
